import { portalApi } from '../../../api';
import { createErrorToast, createSuccessToast, createToast } from '../app';
import { selectCompanyId } from '../company';

const USERS_GET_USER = 'USERS_GET_USER';
const USERS_GET_USER_SUCCESS = 'USERS_GET_USER_SUCCESS';
const USERS_GET_USER_FAILURE = 'USERS_GET_USER_FAILURE';

export const getCurrentUser = () => async (dispatch, getState) => {
  if (getState().user?.userId) return;

  dispatch({ type: USERS_GET_USER });

  try {
    const user = await portalApi.getUser();
    dispatch({ type: USERS_GET_USER_SUCCESS, user });
  } catch (error) {
    dispatch({ type: USERS_GET_USER_FAILURE, error });
    throw error;
  }
};

const GET_USER_COMPANIES = 'GET_USER_COMPANIES';
const GET_USER_COMPANIES_SUCCESS = 'GET_USER_COMPANIES_SUCCESS';
const GET_USER_COMPANIES_FAILURE = 'GET_USER_COMPANIES_FAILURE';

export const getUserCompanies = () => async (dispatch, getState) => {
  if (getState().user?.userId) return;

  dispatch({ type: GET_USER_COMPANIES });

  try {
    const companies = await portalApi.listCompanies();
    dispatch({ type: GET_USER_COMPANIES_SUCCESS, companies });
  } catch (error) {
    dispatch({ type: GET_USER_COMPANIES_FAILURE, error });
    throw error;
  }
};

const USERS_GET_COMPANY_PERMISSION_LEVEL = 'USERS_GET_COMPANY_PERMISSION_LEVEL';
const USERS_GET_COMPANY_PERMISSION_LEVEL_SUCCESS = 'USERS_GET_COMPANY_PERMISSION_LEVEL_SUCCESS';
const USERS_GET_COMPANY_PERMISSION_LEVEL_FAILURE = 'USERS_GET_COMPANY_PERMISSION_LEVEL_FAILURE';

export const getUserCompanyPermissionLevel = companyId => async dispatch => {
  dispatch({ type: USERS_GET_COMPANY_PERMISSION_LEVEL });

  try {
    const permissionLevel = 2;
    dispatch({ type: USERS_GET_COMPANY_PERMISSION_LEVEL_SUCCESS, permissionLevel });
  } catch (error) {
    dispatch({ type: USERS_GET_COMPANY_PERMISSION_LEVEL_FAILURE, error });
  }
};

const USERS_UPDATE_USER_PASSWORD = 'USERS_UPDATE_USER_PASSWORD';
const USERS_UPDATE_USER_PASSWORD_SUCCESS = 'USERS_UPDATE_USER_PASSWORD_SUCCESS';
const USERS_UPDATE_USER_PASSWORD_FAILURE = 'USERS_UPDATE_USER_PASSWORD_FAILURE';

export const updateUserPassword = (userId, existingPassword, newPassword, confirmPassword) => async dispatch => {
  dispatch({ type: USERS_UPDATE_USER_PASSWORD });

  dispatch(createToast({ title: 'Updating password' }));

  try {
    const passwordUpdated = await portalApi.updateUserPassword(userId, existingPassword, newPassword, confirmPassword);
    dispatch({ type: USERS_UPDATE_USER_PASSWORD_SUCCESS, passwordUpdated });

    dispatch(createSuccessToast({ title: 'Password updated successfully!' }));
  } catch (error) {
    dispatch({ type: USERS_UPDATE_USER_PASSWORD_FAILURE, error });

    dispatch(createErrorToast({
      title: 'Password was not updated',
      description: 'Current password is incorrect',
      error
    }));
  }
};

const USERS_UPDATE_USER_INFO = 'USERS_UPDATE_USER_INFO';
const USERS_UPDATE_USER_INFO_SUCCESS = 'USERS_UPDATE_USER_INFO_SUCCESS';
const USERS_UPDATE_USER_INFO_FAILURE = 'USERS_UPDATE_USER_INFO_FAILURE';

export const updateUserBasicInfo = (userId, userInfo) => async dispatch => {
  const userData = {
    user_first_name: userInfo.userFirstName,
    user_last_name: userInfo.userLastName,
    user_phone: userInfo.userPhone,
    user_email: userInfo.userEmail
  };

  dispatch({ type: USERS_UPDATE_USER_INFO, userId, userData });

  try {
    const updatedUser = await portalApi.updateUser(userId, userData);
    dispatch({ type: USERS_UPDATE_USER_INFO_SUCCESS, updatedUser });
  } catch (error) {
    dispatch({ type: USERS_UPDATE_USER_INFO_FAILURE, error });
    return { success: false, message: error.message };
  }

  return { success: true };
};

const USERS_GET_USER_SETTINGS = 'USERS_GET_USER_SETTINGS';
const USERS_GET_USER_SETTINGS_SUCCESS = 'USERS_GET_USER_SETTINGS_SUCCESS';
const USERS_GET_USER_SETTINGS_FAILURE = 'USERS_GET_USER_SETTINGS_FAILURE';
export const getUserSettings = () => async (dispatch, getState) => {
  dispatch({ type: USERS_GET_USER_SETTINGS });

  const companyId = selectCompanyId(getState());
  const { userId } = selectUser(getState());

  try {
    const settings = await portalApi.getUserSettings(userId, companyId);
    dispatch({ type: USERS_GET_USER_SETTINGS_SUCCESS, settings });
  } catch (error) {
    dispatch({ type: USERS_GET_USER_SETTINGS_FAILURE, error });
  }
};

const USERS_UPDATE_USER_EMAIL_SETTINGS = 'USERS_UPDATE_USER_EMAIL_SETTINGS';
const USERS_UPDATE_USER_EMAIL_SETTINGS_SUCCESS = 'USERS_UPDATE_USER_EMAIL_SETTINGS_SUCCESS';
const USERS_UPDATE_USER_EMAIL_SETTINGS_FAILURE = 'USERS_UPDATE_USER_EMAIL_SETTINGS_FAILURE';

export const updateUserSettings = (settingId, data) => async (dispatch, getState) => {
  const userId = selectUserId(getState());
  const companyId = selectCompanyId(getState());

  dispatch({ type: USERS_UPDATE_USER_EMAIL_SETTINGS, settingId, data });

  try {
    const response = await portalApi.updateUserSetting(userId, companyId, settingId, data);
    dispatch({ type: USERS_UPDATE_USER_EMAIL_SETTINGS_SUCCESS, response });
    dispatch(getUserSettings(userId));
    dispatch(createSuccessToast({ title: 'Email Settings saved!' }));
  } catch (error) {
    dispatch({ type: USERS_UPDATE_USER_EMAIL_SETTINGS_FAILURE, error });
  }
};

const USERS_CURRENT_USER_UPDATED = 'USERS_CURRENT_USER_UPDATED';

export const updateCurrentUser = userData => dispatch => {
  dispatch({ type: USERS_CURRENT_USER_UPDATED, userData });
};

const DEFAULT_STATE = {
  permissionLevel: 0,
  roles: [],
  companies: [],
  companiesListLoading: true,
  userId: null,
  settings: null
};

const userReducer = (state = DEFAULT_STATE, action) => {
  switch (action.type) {
    case USERS_GET_USER_SUCCESS: {
      return {
        ...state,
        ...action.user,
        firstName: action.user.userFirstName,
        lastName: action.user.userLastName
      };
    }

    case USERS_GET_USER_FAILURE: {
      return { ...state, error: action.error };
    }

    case USERS_UPDATE_USER_INFO_SUCCESS: {
      return {
        ...state,
        ...action.updatedUser,
        firstName: action.updatedUser.userFirstName,
        lastName: action.updatedUser.userLastName
      };
    }

    case USERS_UPDATE_USER_INFO_FAILURE: {
      return { ...state, error: action.error };
    }

    case USERS_UPDATE_USER_PASSWORD_FAILURE: {
      return { ...state, error: action.error };
    }

    case USERS_GET_USER_SETTINGS_SUCCESS: {
      return { ...state, settings: action.settings };
    }

    case GET_USER_COMPANIES: {
      return {
        ...state,
        companies: action.companies,
        companiesListLoading: true
      };
    }

    case GET_USER_COMPANIES_SUCCESS: {
      return {
        ...state,
        companies: action.companies,
        companiesListLoading: false
      };
    }

    case GET_USER_COMPANIES_FAILURE: {
      return {
        ...state,
        companies: [],
        companiesListLoading: false
      };
    }

    case USERS_CURRENT_USER_UPDATED:
      return {
        ...state,
        ...action.userData,
        firstName: action.userData.userFirstName,
        lastName: action.userData.userLastName
      };

    default:
      return state;
  }
};

export const selectUser = state => state.user;
export const selectUserId = state => state.user.userId;
export const selectUserPermissionLevel = state => state.user.userLevel;
export const selectUserSettings = state => state.user.settings;
export const selectUserRoles = state => state.user.roles;
export const selectUserCompanies = state => state.user.companies;
export const selectCompaniesListLoading = state => state.user.companiesListLoading;
export default userReducer;
